import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/core/utils';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { Sun, Moon, CloudRain, CloudSnow, Zap, Cloud, CloudSun } from 'lucide-react';
import { BusinessDetails } from '@/types/index';

// Two-color CloudSun component wrapper
const TwoColorCloudSun: React.FC<{ className?: string }> = ({ className = "" }) => {
  // Extract size classes from className
  const sizeMatch = className.match(/h-(\d+)\s+w-(\d+)/);
  const size = sizeMatch ? sizeMatch[1] : '8';
  
  return (
    <div className="relative inline-flex">
      {/* Base cloud in grey */}
      <CloudSun className={cn(className, "text-gray-400")} />
      {/* Sun overlay in yellow - positioned to only show the sun part */}
      <CloudSun 
        className={cn(className, "absolute inset-0 text-yellow-300")}
        style={{
          clipPath: size === '8' 
            ? 'polygon(-15% 0%, 100% 0%, 100% 55%, 100% 90%)' // Larger icon - sun is in upper-right corner
            : 'polygon(-15% 0%, 100% 0%, 100% 55%, 100% 90%)' // Smaller icon - sun is in upper-right corner
        }}
      />
    </div>
  );
};

interface WeatherInfo {
  temperature: string;
  condition: string;
  icon: string;
  forecast: string;
  location?: string;
}

interface DayForecast {
  date: string;
  dayName: string;
  high: string;
  low: string;
  highTemp: number;
  lowTemp: number;
  condition: string;
  icon: string;
  rainChance: number;
  currentTemp?: number;
}

type TimePeriod = 'night' | 'dawn' | 'morning' | 'afternoon' | 'golden-hour' | 'sunset-peak' | 'afterglow' | 'nautical-dusk' | 'deep-dusk' | 'early-night';

interface CurrentTimeInfo {
  currentTime: string;
  currentDate: string;
  weather: WeatherInfo | null;
  sunrise: Date | null;
  sunset: Date | null;
  timePeriod: TimePeriod;
  weekForecast: DayForecast[];
}

interface WeatherTimeInfoCardProps {
  businessDetails: BusinessDetails | null;
  businessStatus: {
    statusText: string;
    openUntil?: string;
    reopensAt?: string;
  } | null;
}

// Function to determine the current time period based on sunrise/sunset
const getTimePeriod = (currentTime: Date, sunrise: Date | null, sunset: Date | null): TimePeriod => {
  if (!sunrise || !sunset) {
    // Fallback to hour-based detection if sunrise/sunset not available
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 7) return 'dawn';
    if (hour >= 7 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 16) return 'afternoon';
    if (hour >= 16 && hour < 18) return 'golden-hour';
    if (hour >= 18 && hour < 19) return 'sunset-peak';
    if (hour >= 19 && hour < 20) return 'afterglow';
    if (hour >= 20 && hour < 21) return 'nautical-dusk';
    if (hour >= 21 && hour < 22) return 'deep-dusk';
    if (hour >= 22 && hour < 23) return 'early-night';
    return 'night';
  }

  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
  const sunriseMinutes = sunrise.getHours() * 60 + sunrise.getMinutes();
  const sunsetMinutes = sunset.getHours() * 60 + sunset.getMinutes();

  // Define precise time periods relative to sunrise/sunset
  const dawnStart = sunriseMinutes - 30; // 30 minutes before sunrise
  const morningStart = sunriseMinutes; // Sunrise
  const afternoonStart = 12 * 60; // Noon
  const goldenHourStart = sunsetMinutes - 120; // 2 hours before sunset (golden hour)
  const sunsetPeakStart = sunsetMinutes - 30; // 30 minutes before sunset
  const afterglowStart = sunsetMinutes + 10; // 10 minutes after sunset
  const nauticalDuskStart = sunsetMinutes + 30; // 30 minutes after sunset
  const deepDuskStart = sunsetMinutes + 60; // 60 minutes after sunset
  const earlyNightStart = sunsetMinutes + 90; // 90 minutes after sunset

  // Determine current time period
  if (currentMinutes >= earlyNightStart || currentMinutes < dawnStart) {
    return 'night';
  } else if (currentMinutes >= dawnStart && currentMinutes < morningStart) {
    return 'dawn';
  } else if (currentMinutes >= morningStart && currentMinutes < afternoonStart) {
    return 'morning';
  } else if (currentMinutes >= afternoonStart && currentMinutes < goldenHourStart) {
    return 'afternoon';
  } else if (currentMinutes >= goldenHourStart && currentMinutes < sunsetPeakStart) {
    return 'golden-hour';
  } else if (currentMinutes >= sunsetPeakStart && currentMinutes < afterglowStart) {
    return 'sunset-peak';
  } else if (currentMinutes >= afterglowStart && currentMinutes < nauticalDuskStart) {
    return 'afterglow';
  } else if (currentMinutes >= nauticalDuskStart && currentMinutes < deepDuskStart) {
    return 'nautical-dusk';
  } else if (currentMinutes >= deepDuskStart && currentMinutes < earlyNightStart) {
    return 'deep-dusk';
  } else {
    return 'early-night';
  }
};

// Function to get background gradient class based on time period and weather
const getBackgroundGradientClass = (timePeriod: TimePeriod, weatherIcon: string | undefined): string => {
  // Determine weather condition modifier
  let weatherModifier = '';
  if (weatherIcon?.includes('rain') || weatherIcon?.includes('drizzle') || weatherIcon?.includes('showers')) {
    weatherModifier = ' rainy';
  } else if (weatherIcon === 'snow') {
    weatherModifier = ' snowy';
  } else if (weatherIcon === 'storm') {
    weatherModifier = ' storm';
  } else if (weatherIcon === 'cloud') {
    weatherModifier = ' cloudy';
  }

  // Map time periods to CSS classes
  switch (timePeriod) {
    case 'golden-hour':
      return `bg-golden-hour${weatherModifier}`;
    case 'sunset-peak':
      return `bg-sunset-peak${weatherModifier}`;
    case 'afterglow':
      return `bg-afterglow${weatherModifier}`;
    case 'nautical-dusk':
      return `bg-nautical-dusk${weatherModifier}`;
    case 'deep-dusk':
      return `bg-deep-dusk${weatherModifier}`;
    case 'early-night':
      return `bg-early-night${weatherModifier}`;
    case 'night':
      return `bg-early-night${weatherModifier}`; // Use early-night for deep night
    case 'dawn':
      // Mirror sunset gradients for dawn (reverse order for sunrise)
      return `bg-afterglow${weatherModifier}`;
    case 'morning':
      return 'bg-gradient-to-b from-sky-400 via-blue-400 to-blue-500'; // Keep existing morning
    case 'afternoon':
      return 'bg-gradient-to-b from-blue-500 via-sky-400 to-blue-400'; // Keep existing afternoon
    default:
      return 'bg-gradient-to-b from-gray-400 via-gray-500 to-gray-600';
  }
};

// Function to get color for a specific temperature
const getTemperatureColor = (temp: number): string => {
  if (temp <= -5) return '#1E40AF'; // blue-800 - deep blue for very cold
  if (temp <= 0) {
    // Interpolate between deep blue and blue
    const ratio = (temp + 5) / 5;
    return `rgb(${30 + (59 - 30) * ratio}, ${64 + (130 - 64) * ratio}, ${175 + (246 - 175) * ratio})`;
  }
  if (temp <= 5) {
    // Interpolate between blue and cyan
    const ratio = temp / 5;
    return `rgb(${59 + (6 - 59) * ratio}, ${130 + (182 - 130) * ratio}, ${246 + (212 - 246) * ratio})`;
  }
  if (temp <= 10) {
    // Interpolate between cyan and light blue/teal
    const ratio = (temp - 5) / 5;
    return `rgb(${6 + (20 - 6) * ratio}, ${182 + (184 - 182) * ratio}, ${212 + (166 - 212) * ratio})`;
  }
  if (temp <= 15) {
    // Green starts here - interpolate between teal and green
    const ratio = (temp - 10) / 5;
    return `rgb(${20 + (16 - 20) * ratio}, ${184 + (185 - 184) * ratio}, ${166 + (129 - 166) * ratio})`;
  }
  if (temp <= 20) {
    // Interpolate between green and light green
    const ratio = (temp - 15) / 5;
    return `rgb(${16 + (132 - 16) * ratio}, ${185 + (204 - 185) * ratio}, ${129 + (9 - 129) * ratio})`;
  }
  if (temp <= 25) {
    // Interpolate between light green and yellow
    const ratio = (temp - 20) / 5;
    return `rgb(${132 + (234 - 132) * ratio}, ${204 + (179 - 204) * ratio}, ${9 + (8 - 9) * ratio})`;
  }
  if (temp <= 30) {
    // Interpolate between yellow and orange
    const ratio = (temp - 25) / 5;
    return `rgb(${234 + (249 - 234) * ratio}, ${179 + (115 - 179) * ratio}, ${8 + (22 - 8) * ratio})`;
  }
  // 30°C and above - solid red
  return '#EF4444'; // red-500
};

// Function to generate gradient string for temperature range
const generateTemperatureGradient = (lowTemp: number, highTemp: number): string => {
  const steps = 10; // Number of gradient stops
  const gradientStops: string[] = [];
  
  for (let i = 0; i <= steps; i++) {
    const temp = lowTemp + (highTemp - lowTemp) * (i / steps);
    const color = getTemperatureColor(temp);
    const percentage = (i / steps) * 100;
    gradientStops.push(`${color} ${percentage}%`);
  }
  
  return `linear-gradient(to right, ${gradientStops.join(', ')})`;
};


const WeatherTimeInfoCard: React.FC<WeatherTimeInfoCardProps> = ({
  businessDetails,
  businessStatus
}) => {
  const isMobile = useIsMobile();
  const [rainDrops, setRainDrops] = useState<string>('');

  // Generate realistic rain drops based on intensity
  const generateRainDrops = (intensity: string) => {
    let drops = '';
    let increment = 0;

    // Different settings for each rain type
    const rainSettings = {
      'drizzle-light': { count: 30, speed: '0.8', opacity: 0.4, stemWidth: '0.5px' },
      'drizzle-moderate': { count: 50, speed: '0.7', opacity: 0.5, stemWidth: '0.8px' },
      'drizzle-heavy': { count: 70, speed: '0.6', opacity: 0.6, stemWidth: '1px' },
      'drizzle-freezing': { count: 40, speed: '0.9', opacity: 0.7, stemWidth: '0.8px' },
      'rain-light': { count: 60, speed: '0.5', opacity: 0.6, stemWidth: '1px' },
      'rain-moderate': { count: 80, speed: '0.4', opacity: 0.7, stemWidth: '1.2px' },
      'rain-heavy': { count: 120, speed: '0.3', opacity: 0.8, stemWidth: '1.5px' },
      'rain-freezing': { count: 70, speed: '0.6', opacity: 0.8, stemWidth: '1.2px' },
      'showers-light': { count: 50, speed: '0.4', opacity: 0.6, stemWidth: '1px' },
      'showers-moderate': { count: 80, speed: '0.3', opacity: 0.7, stemWidth: '1.3px' },
      'showers-heavy': { count: 140, speed: '0.2', opacity: 0.9, stemWidth: '1.8px' }
    };

    const settings = rainSettings[intensity as keyof typeof rainSettings] || rainSettings['rain-moderate'];
    const targetCount = settings.count;

    while (increment < 100 && drops.split('class="rain-drop"').length - 1 < targetCount) {
      const randoHundo = Math.floor(Math.random() * (98 - 1 + 1) + 1);
      const randoFiver = Math.floor(Math.random() * (5 - 2 + 1) + 2);
      increment += randoFiver;

      const left = increment;
      const bottom = randoFiver + randoFiver - 1 + 100;
      const delay = `0.${randoHundo}s`;
      const duration = `${settings.speed}${randoHundo}s`;

      // Special effects for different rain types
      let stemStyle = `animation-delay: ${delay}; animation-duration: ${duration}; width: ${settings.stemWidth}; opacity: ${settings.opacity};`;
      let splatStyle = `animation-delay: ${delay}; animation-duration: ${duration}; opacity: ${settings.opacity};`;

      // Freezing rain has icy blue tint
      if (intensity.includes('freezing')) {
        stemStyle += ' filter: hue-rotate(180deg) brightness(1.2);';
        splatStyle += ' filter: hue-rotate(180deg) brightness(1.2); border-color: rgba(173, 216, 230, 0.8);';
      }

      drops += `
        <div class="rain-drop" style="left: ${left}%; bottom: ${bottom}%; animation-delay: ${delay}; animation-duration: ${duration};">
          <div class="rain-stem" style="${stemStyle}"></div>
          <div class="rain-splat" style="${splatStyle}"></div>
        </div>
      `;
    }

    return drops;
  };

  const [currentTimeInfo, setCurrentTimeInfo] = useState<CurrentTimeInfo>({
    currentTime: new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }),
    currentDate: new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    weather: null,
    sunrise: null,
    sunset: null,
    timePeriod: 'morning',
    weekForecast: []
  });

  const updateCurrentTimeInfo = async () => {
    const now = new Date();
    const timeInfo: CurrentTimeInfo = {
      currentTime: now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      }),
      currentDate: now.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      weather: null,
      sunrise: null,
      sunset: null,
      timePeriod: 'morning',
      weekForecast: []
    };

    // Fetch weather data if business location is available
    if (businessDetails?.address) {
      try {
        // Extract city from address - completely dynamic approach
        const address = businessDetails.address;
        const parts = address.split(',').map(part => part.trim());
        
        let citySearch = '';
        let countrySearch = '';
        
        // Country mapping for better geocoding results
        const countryMapping: { [key: string]: string } = {
          'Slovakia': 'Slovakia',
          'Czech Republic': 'Czech Republic',
          'Poland': 'Poland',
          'Hungary': 'Hungary',
          'Austria': 'Austria',
          'Germany': 'Germany',
          'USA': 'United States',
          'United States': 'United States',
          'Canada': 'Canada',
          'UK': 'United Kingdom',
          'United Kingdom': 'United Kingdom'
        };
        
        // Find country in address parts
        for (const part of parts) {
          if (countryMapping[part]) {
            countrySearch = countryMapping[part];
            break;
          }
        }
        
        // Simple city extraction - find city in address parts
        for (let i = parts.length - 1; i >= 0; i--) {
          const part = parts[i];
          
          // Skip parts that are clearly not cities
          if (part.length < 2 || // Too short
              /^\d+$/.test(part) || // Only numbers (postal codes)
              /^\d+\s/.test(part) || // Starts with numbers (street addresses)
              /\d{4,}/.test(part) || // Contains 4+ digit postal codes
              countryMapping[part]) { // Skip country names
            continue;
          }
          
          // Use this part as the city name for display
          citySearch = part;
          break;
        }
        
        // If no city found, try second-to-last part
        if (!citySearch && parts.length > 1) {
          const secondLast = parts[parts.length - 2];
          if (secondLast && secondLast.length >= 2 && !countryMapping[secondLast]) {
            citySearch = secondLast;
          }
        }
        
        // Final fallback: use first non-empty part that's not a country
        if (!citySearch) {
          for (const part of parts) {
            if (part && part.length >= 2 && !countryMapping[part] && !/^\d+/.test(part)) {
              citySearch = part;
              break;
            }
          }
        }
        
        // Map country names to ISO country codes for geocoding API
        const countryCodeMapping: { [key: string]: string } = {
          'Slovakia': 'SK',
          'Czech Republic': 'CZ',  
          'Poland': 'PL',
          'Hungary': 'HU',
          'Austria': 'AT',
          'Germany': 'DE',
          'United States': 'US',
          'Canada': 'CA',
          'United Kingdom': 'GB'
        };
        
        const countryCode = countrySearch ? countryCodeMapping[countrySearch] : '';
        
        // OpenMeteo geocoding API - using countryCode parameter for better results
        // Note: count=1 sometimes returns empty results, using count=10 and taking first result
        const geocodeUrl = countryCode 
          ? `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(citySearch)}&countryCode=${countryCode}&count=10&format=json&language=en`
          : `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(citySearch)}&count=10&format=json&language=en`;
        
        const geocodeResponse = await fetch(geocodeUrl);
        
        let latitude: number | null = null;
        let longitude: number | null = null;
        
        if (geocodeResponse.ok) {
          const geocodeData = await geocodeResponse.json();
          
          if (geocodeData.results && geocodeData.results.length > 0) {
            const result = geocodeData.results[0];
            latitude = result.latitude;
            longitude = result.longitude;
          } else {
            // Fallback: try without country code if we had one
            if (countryCode) {
              const fallbackUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(citySearch)}&count=10&format=json&language=en`;
              const fallbackResponse = await fetch(fallbackUrl);
              if (fallbackResponse.ok) {
                const fallbackData = await fallbackResponse.json();
                if (fallbackData.results && fallbackData.results.length > 0) {
                  const fallbackResult = fallbackData.results[0];
                  latitude = fallbackResult.latitude;
                  longitude = fallbackResult.longitude;
                }
              }
            }
          }
        }
        
        // Only proceed if we successfully geocoded the address
        if (latitude && longitude) {
          let weatherData = null;
          // Use simple city name from parsed address for display (or empty if not found)
          const locationName = citySearch || '';
          
          // Try Apple WeatherKit first via our pass-generator-server
          try {
            const PASS_GENERATOR_URL = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://localhost:3001';
            const weatherKitUrl = `${PASS_GENERATOR_URL}/api/weather?lat=${latitude}&lon=${longitude}`;
            const weatherKitResponse = await fetch(weatherKitUrl);
            
            if (weatherKitResponse.ok) {
              const weatherKitData = await weatherKitResponse.json();
              
              timeInfo.weather = {
                temperature: weatherKitData.temperature,
                condition: weatherKitData.condition,
                icon: weatherKitData.icon,
                forecast: weatherKitData.forecast,
                location: locationName
              };
              
              // Extract sunrise/sunset from WeatherKit
              if (weatherKitData.sunrise) {
                timeInfo.sunrise = new Date(weatherKitData.sunrise);
              }
              if (weatherKitData.sunset) {
                timeInfo.sunset = new Date(weatherKitData.sunset);
              }
              
              weatherData = weatherKitData; // Mark as successful

              // Fetch week forecast from Open-Meteo (WeatherKit only provides current day)
              try {
                const forecastUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto&forecast_days=5`;
                const forecastResponse = await fetch(forecastUrl);

                if (forecastResponse.ok) {
                  const forecastData = await forecastResponse.json();
                  const dailyWeather = forecastData.daily;

                  // Helper to map Open-Meteo weather codes
                  const mapWeatherCode = (code: number) => {
                    if (code === 0) return { condition: 'Clear Sky', icon: 'sun' };
                    if (code >= 1 && code <= 3) return { condition: 'Partly Cloudy', icon: 'cloud' };
                    if (code >= 45 && code <= 48) return { condition: 'Foggy', icon: 'cloud' };
                    if (code === 51) return { condition: 'Light Drizzle', icon: 'drizzle-light' };
                    if (code === 53) return { condition: 'Moderate Drizzle', icon: 'drizzle-moderate' };
                    if (code === 55) return { condition: 'Dense Drizzle', icon: 'drizzle-heavy' };
                    if (code === 56 || code === 57) return { condition: 'Freezing Drizzle', icon: 'drizzle-freezing' };
                    if (code === 61) return { condition: 'Light Rain', icon: 'rain-light' };
                    if (code === 63) return { condition: 'Moderate Rain', icon: 'rain-moderate' };
                    if (code === 65) return { condition: 'Heavy Rain', icon: 'rain-heavy' };
                    if (code === 66 || code === 67) return { condition: 'Freezing Rain', icon: 'rain-freezing' };
                    if (code === 80) return { condition: 'Light Showers', icon: 'showers-light' };
                    if (code === 81) return { condition: 'Moderate Showers', icon: 'showers-moderate' };
                    if (code === 82) return { condition: 'Heavy Showers', icon: 'showers-heavy' };
                    if (code >= 71 && code <= 77) return { condition: 'Snowy', icon: 'snow' };
                    if (code >= 95 && code <= 99) return { condition: 'Thunderstorm', icon: 'storm' };
                    return { condition: 'Unknown', icon: 'cloud' };
                  };

                  timeInfo.weekForecast = [];
                  for (let i = 1; i < Math.min(5, dailyWeather.time.length); i++) {
                    const forecastDate = new Date(dailyWeather.time[i]);
                    const forecastWeatherInfo = mapWeatherCode(dailyWeather.weather_code[i]);
                    const high = Math.round(dailyWeather.temperature_2m_max[i]);
                    const low = Math.round(dailyWeather.temperature_2m_min[i]);
                    const precipitation = dailyWeather.precipitation_sum ? dailyWeather.precipitation_sum[i] : 0;
                    const rainChance = Math.min(Math.round(precipitation * 10), 100);
                    const dayName = forecastDate.toLocaleDateString('en-US', { weekday: 'short' });

                    timeInfo.weekForecast.push({
                      date: dailyWeather.time[i],
                      dayName: dayName,
                      high: `${high}°`,
                      low: `${low}°`,
                      highTemp: high,
                      lowTemp: low,
                      condition: forecastWeatherInfo.condition,
                      icon: forecastWeatherInfo.icon,
                      rainChance: rainChance
                    });
                  }
                }
              } catch {
                // Forecast fetch failed, continue without it
              }
            } else if (weatherKitResponse.status === 503) {
              // WeatherKit service temporarily unavailable, fallback gracefully
            } else {
              // WeatherKit API error
            }
          } catch (error) {
            // Apple WeatherKit failed, falling back to other sources
          }
          
          // Fallback to OpenWeatherMap if WeatherKit failed
          if (!weatherData) {
            const OPENWEATHER_API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;
            
            if (OPENWEATHER_API_KEY) {
              try {
                const owmUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${OPENWEATHER_API_KEY}&units=metric`;
                const owmResponse = await fetch(owmUrl);
                
                if (owmResponse.ok) {
                  const owmData = await owmResponse.json();
                  
                  // Convert OpenWeatherMap data to our format
                  const getOWMWeatherInfo = (description: string, id: number) => {
                    // Map OpenWeatherMap conditions to our weather icons
                    if (id >= 200 && id < 300) return { condition: description, icon: 'storm' };
                    if (id >= 300 && id < 400) return { condition: description, icon: 'drizzle-light' };
                    if (id >= 500 && id < 600) {
                      if (id === 500) return { condition: description, icon: 'rain-light' };
                      if (id === 501) return { condition: description, icon: 'rain-moderate' };
                      if (id >= 502) return { condition: description, icon: 'rain-heavy' };
                      return { condition: description, icon: 'rain-moderate' };
                    }
                    if (id >= 600 && id < 700) return { condition: description, icon: 'snow' };
                    if (id >= 700 && id < 800) return { condition: description, icon: 'cloud' };
                    if (id === 800) return { condition: description, icon: 'sun' };
                    if (id > 800) return { condition: description, icon: 'cloud' };
                    return { condition: description, icon: 'cloud' };
                  };
                  
                  const currentWeatherInfo = getOWMWeatherInfo(
                    owmData.weather[0].description,
                    owmData.weather[0].id
                  );
                  
                  const currentTemp = Math.round(owmData.main.temp);
                  const maxTemp = Math.round(owmData.main.temp_max);
                  const minTemp = Math.round(owmData.main.temp_min);
                  
                  timeInfo.weather = {
                    temperature: `${currentTemp}°C`,
                    condition: currentWeatherInfo.condition,
                    icon: currentWeatherInfo.icon,
                    forecast: `H: ${maxTemp}°C  L: ${minTemp}°C`,
                    location: locationName
                  };
                  
                  
                  // OpenWeatherMap provides sunrise/sunset in sys object (unix timestamp)
                  if (owmData.sys && owmData.sys.sunrise) {
                    timeInfo.sunrise = new Date(owmData.sys.sunrise * 1000);
                  }
                  if (owmData.sys && owmData.sys.sunset) {
                    timeInfo.sunset = new Date(owmData.sys.sunset * 1000);
                  }
                  
                  weatherData = owmData; // Mark as successful
                }
              } catch (error) {
                // OpenWeatherMap failed, falling back to Open-Meteo
              }
            }
          }
          
          // Final fallback to Open-Meteo if both WeatherKit and OpenWeatherMap failed
          if (!weatherData) {
            const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,sunrise,sunset&timezone=auto&forecast_days=5`;
            
            const weatherResponse = await fetch(weatherUrl);
            
            if (weatherResponse.ok) {
              const openMeteoData = await weatherResponse.json();
              
              const currentWeather = openMeteoData.current;
              const dailyWeather = openMeteoData.daily;
            
              // Convert weather code to condition with detailed rain states
              const getWeatherInfo = (code: number) => {
                if (code === 0) return { condition: 'Clear Sky', icon: 'sun' };
                if (code >= 1 && code <= 3) return { condition: 'Partly Cloudy', icon: 'cloud' };
                if (code >= 45 && code <= 48) return { condition: 'Foggy', icon: 'cloud' };
                
                // Drizzle codes
                if (code === 51) return { condition: 'Light Drizzle', icon: 'drizzle-light' };
                if (code === 53) return { condition: 'Moderate Drizzle', icon: 'drizzle-moderate' };
                if (code === 55) return { condition: 'Dense Drizzle', icon: 'drizzle-heavy' };
                if (code === 56 || code === 57) return { condition: 'Freezing Drizzle', icon: 'drizzle-freezing' };
                
                // Rain codes
                if (code === 61) return { condition: 'Light Rain', icon: 'rain-light' };
                if (code === 63) return { condition: 'Moderate Rain', icon: 'rain-moderate' };
                if (code === 65) return { condition: 'Heavy Rain', icon: 'rain-heavy' };
                if (code === 66 || code === 67) return { condition: 'Freezing Rain', icon: 'rain-freezing' };
                
                // Rain showers
                if (code === 80) return { condition: 'Light Showers', icon: 'showers-light' };
                if (code === 81) return { condition: 'Moderate Showers', icon: 'showers-moderate' };
                if (code === 82) return { condition: 'Heavy Showers', icon: 'showers-heavy' };
                
                if (code >= 71 && code <= 77) return { condition: 'Snowy', icon: 'snow' };
                if (code >= 95 && code <= 99) return { condition: 'Thunderstorm', icon: 'storm' };
                return { condition: 'Unknown', icon: 'cloud' };
              };
              
              const currentWeatherInfo = getWeatherInfo(currentWeather.weather_code);
              
              const maxTemp = Math.round(dailyWeather.temperature_2m_max[0]);
              const minTemp = Math.round(dailyWeather.temperature_2m_min[0]);
              const currentTemp = Math.round(currentWeather.temperature_2m);
              
              timeInfo.weather = {
                temperature: `${currentTemp}°C`,
                condition: currentWeatherInfo.condition,
                icon: currentWeatherInfo.icon,
                forecast: `H: ${maxTemp}°C  L: ${minTemp}°C`,
                location: locationName
              };
              
              // Process forecast data for upcoming days (skip today, show next 4 days)
              timeInfo.weekForecast = [];
              for (let i = 1; i < Math.min(5, dailyWeather.time.length); i++) {
                const forecastDate = new Date(dailyWeather.time[i]);
                const forecastWeatherInfo = getWeatherInfo(dailyWeather.weather_code[i]);
                const high = Math.round(dailyWeather.temperature_2m_max[i]);
                const low = Math.round(dailyWeather.temperature_2m_min[i]);
                
                // Calculate rain chance based on precipitation
                const precipitation = dailyWeather.precipitation_sum ? dailyWeather.precipitation_sum[i] : 0;
                const rainChance = Math.min(Math.round(precipitation * 10), 100); // Simple conversion
                
                const dayName = forecastDate.toLocaleDateString('en-US', { weekday: 'short' });
                
                timeInfo.weekForecast.push({
                  date: dailyWeather.time[i],
                  dayName: dayName,
                  high: `${high}°`,
                  low: `${low}°`,
                  highTemp: high,
                  lowTemp: low,
                  condition: forecastWeatherInfo.condition,
                  icon: forecastWeatherInfo.icon,
                  rainChance: rainChance
                });
              }
              
              // Extract sunrise/sunset from daily data
              if (dailyWeather.sunrise && dailyWeather.sunrise[0]) {
                timeInfo.sunrise = new Date(dailyWeather.sunrise[0]);
              }
              if (dailyWeather.sunset && dailyWeather.sunset[0]) {
                timeInfo.sunset = new Date(dailyWeather.sunset[0]);
              }
              
            }
          }
        }
      } catch (error) {
        // Weather fetch failed silently
      }
    }


    // Calculate time period based on sunrise/sunset
    timeInfo.timePeriod = getTimePeriod(now, timeInfo.sunrise, timeInfo.sunset);
    
    // Adjust weather icon for night time - clear sky at night shouldn't show sun
    if (timeInfo.weather && timeInfo.weather.icon === 'sun' && timeInfo.timePeriod === 'night') {
      timeInfo.weather.icon = 'clear-night';
    }
    
    setCurrentTimeInfo(timeInfo);
  };

  useEffect(() => {
    // Update once on component mount
    updateCurrentTimeInfo();
  }, [businessDetails?.address]);

  // Generate rain drops when weather changes
  useEffect(() => {
    if (currentTimeInfo?.weather?.icon?.includes('rain') ||
        currentTimeInfo?.weather?.icon?.includes('drizzle') ||
        currentTimeInfo?.weather?.icon?.includes('showers') ||
        currentTimeInfo?.weather?.icon === 'storm') {
      setRainDrops(generateRainDrops(currentTimeInfo.weather.icon === 'storm' ? 'rain-heavy' : currentTimeInfo.weather.icon));
    }
  }, [currentTimeInfo?.weather?.icon]);

  // Check if we're in loading state
  const isLoading = !businessStatus || !currentTimeInfo.weather;

  return (
    <Card className={cn(
      "relative overflow-hidden h-full",
      isLoading && "animate-pulse"
    )}>
      {/* iPhone-style animated background */}
      <div className={cn(
        "absolute inset-0 pointer-events-none transition-all duration-1000",
        isLoading ? 'bg-gradient-to-b from-gray-300 via-gray-400 to-gray-500' :
        !currentTimeInfo.weather ? '' :
        getBackgroundGradientClass(currentTimeInfo.timePeriod, currentTimeInfo.weather?.icon)
      )}>
        {/* Time-based visual effects */}

        {/* Sun animations completely disabled to prevent any sun appearance at night */}
        
        {(currentTimeInfo.weather?.icon?.includes('rain') ||
          currentTimeInfo.weather?.icon?.includes('drizzle') ||
          currentTimeInfo.weather?.icon?.includes('showers') ||
          currentTimeInfo.weather?.icon === 'storm') && (
          <>
            {/* Dark rain clouds */}
            <div className="absolute inset-0 pointer-events-none z-10">
              <div
                className="absolute bg-no-repeat bg-contain opacity-80"
                style={{
                  top: '-25%',
                  left: '-35%',
                  width: '380px',
                  height: '250px',
                  backgroundImage: 'url(/media/cloud-large.png)',
                  transform: 'scale(1.84)',
                  filter: currentTimeInfo.timePeriod === 'night' ? 'brightness(0.2) contrast(1.3) saturate(0.5)' :
                         currentTimeInfo.timePeriod === 'dawn' || currentTimeInfo.timePeriod === 'dusk' ? 'brightness(0.3) contrast(1.2) saturate(0.6)' :
                         'brightness(0.4) contrast(1.2) saturate(0.8)'
                }}
              />
              <div
                className="absolute bg-no-repeat bg-contain opacity-70"
                style={{
                  top: '5%',
                  right: '-45%',
                  width: '340px',
                  height: '220px',
                  backgroundImage: 'url(/media/cloud-small.png)',
                  transform: 'scale(2.07)',
                  filter: currentTimeInfo.timePeriod === 'night' ? 'brightness(0.15) contrast(1.4) saturate(0.4)' :
                         currentTimeInfo.timePeriod === 'dawn' || currentTimeInfo.timePeriod === 'dusk' ? 'brightness(0.25) contrast(1.3) saturate(0.5)' :
                         'brightness(0.3) contrast(1.3) saturate(0.7)'
                }}
              />
              <div
                className="absolute bg-no-repeat bg-contain opacity-60"
                style={{
                  top: '35%',
                  left: '5%',
                  width: '360px',
                  height: '240px',
                  backgroundImage: 'url(/media/cloud-large.png)',
                  transform: 'scale(1.61)',
                  filter: currentTimeInfo.timePeriod === 'night' ? 'brightness(0.3) contrast(1.2) saturate(0.4)' :
                         currentTimeInfo.timePeriod === 'dawn' || currentTimeInfo.timePeriod === 'dusk' ? 'brightness(0.4) contrast(1.1) saturate(0.5)' :
                         'brightness(0.5) contrast(1.1) saturate(0.6)'
                }}
              />
            </div>
            {/* Realistic rain animation on top of clouds */}
            <div 
              className="realistic-rain"
              dangerouslySetInnerHTML={{ __html: rainDrops }}
            />
          </>
        )}
        
        {currentTimeInfo.weather?.icon === 'snow' && (
          <div className="absolute inset-0 iphone-snow-animation"></div>
        )}

        {currentTimeInfo.weather?.icon === 'storm' && (
          <div className="absolute inset-0 iphone-storm-animation z-10"></div>
        )}
        
        {currentTimeInfo.weather?.icon === 'cloud' && (
          <div className="absolute inset-0 pointer-events-none z-10">
            {/* Large cloud - covering left side and extending beyond */}
            <div
              className="absolute bg-no-repeat bg-contain opacity-70"
              style={{
                top: '-20%',
                left: '-40%',
                width: '400px',
                height: '260px',
                backgroundImage: 'url(/media/cloud-large.png)',
                transform: 'scale(1.725)',
                filter: 'brightness(1.1) contrast(0.9)'
              }}
            />
            {/* Small cloud - covering center and right, overlapping */}
            <div
              className="absolute bg-no-repeat bg-contain opacity-60"
              style={{
                top: '10%',
                right: '-50%',
                width: '320px',
                height: '200px',
                backgroundImage: 'url(/media/cloud-small.png)',
                transform: 'scale(1.955)',
                filter: 'brightness(1.1) contrast(0.9)'
              }}
            />
            {/* Additional large cloud for full coverage */}
            <div
              className="absolute bg-no-repeat bg-contain opacity-50"
              style={{
                top: '40%',
                left: '10%',
                width: '350px',
                height: '230px',
                backgroundImage: 'url(/media/cloud-large.png)',
                transform: 'scale(1.495)',
                filter: 'brightness(1.2) contrast(0.8)'
              }}
            />
          </div>
        )}
        
        {/* Stars for night time */}
        {(currentTimeInfo.timePeriod === 'night' ||
          currentTimeInfo.timePeriod === 'early-night' ||
          currentTimeInfo.timePeriod === 'deep-dusk' ||
          currentTimeInfo.timePeriod === 'nautical-dusk') && (
          <div className="absolute inset-0 iphone-stars-animation z-0"></div>
        )}
        
        {/* Moon completely removed - NO SUN-LIKE OBJECTS AT NIGHT */}
      </div>

      {/* Semi-transparent overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
      
      <CardHeader className={cn(
        "flex space-y-0 pb-3 flex-shrink-0 relative z-10",
        isMobile ? "flex-col space-y-2" : "flex-row items-center justify-between"
      )}>
        <div>
          <CardTitle className="text-sm font-medium text-white">Weather</CardTitle>
        </div>
        {!isMobile && (
          <div>
            <CardTitle className="text-sm font-medium text-white">Forecast</CardTitle>
          </div>
        )}
      </CardHeader>
      <CardContent className={cn(
        "relative z-10",
        isMobile ? "space-y-4" : "grid grid-cols-2 gap-4"
      )}>
        {isLoading ? (
          <>
            <div className="space-y-3">
              <div className="h-12 bg-white/20 rounded-md w-24"></div>
              <div className="h-4 bg-white/15 rounded-md w-32"></div>
              <div className="h-3 bg-white/15 rounded-md w-28"></div>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-white/15 rounded-md"></div>
              <div className="h-3 bg-white/15 rounded-md"></div>
              <div className="h-3 bg-white/15 rounded-md"></div>
            </div>
          </>
        ) : (
          <>
            {/* Left: Current Weather - Prominent Display */}
            <div className="flex flex-col justify-center space-y-2">
              {currentTimeInfo.weather && (
                <>
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "font-bold text-white",
                      isMobile ? "text-3xl" : "text-4xl"
                    )}>
                      {currentTimeInfo.weather.temperature}
                    </div>
                    {currentTimeInfo.weather?.icon === 'clear-night' ? (
                      <Moon className="h-8 w-8 text-yellow-200" />
                    ) : currentTimeInfo.weather?.icon === 'sun' ? (
                      <Sun className={cn(
                        "text-yellow-300",
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    ) : (currentTimeInfo.weather?.icon?.includes('rain') || 
                         currentTimeInfo.weather?.icon?.includes('drizzle') || 
                         currentTimeInfo.weather?.icon?.includes('showers')) ? (
                      <CloudRain className={cn(
                        "text-blue-300",
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    ) : currentTimeInfo.weather?.icon === 'snow' ? (
                      <CloudSnow className={cn(
                        "text-blue-200",
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    ) : currentTimeInfo.weather?.icon === 'storm' ? (
                      <Zap className={cn(
                        "text-yellow-300",
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    ) : currentTimeInfo.weather?.icon === 'cloud' ? (
                      <TwoColorCloudSun className={cn(
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    ) : (
                      <Cloud className={cn(
                        "text-gray-300",
                        isMobile ? "h-6 w-6" : "h-8 w-8"
                      )} />
                    )}
                  </div>
                  <div className="text-sm text-white/90">{currentTimeInfo.weather.condition}</div>
                  <div className="text-xs text-white/70">
                    {currentTimeInfo.weather.forecast}
                    {currentTimeInfo.weather.location && currentTimeInfo.weather.location.trim() && ` - ${currentTimeInfo.weather.location}`}
                  </div>
                </>
              )}
            </div>

            {/* Right: Weather Forecast */}
            <div className={cn(
              isMobile ? "space-y-3" : "space-y-2"
            )}>
              {isMobile && (
                <div className="text-sm font-medium text-white mb-2">4-Day Forecast</div>
              )}
              {currentTimeInfo.weather && currentTimeInfo.weekForecast.length > 0 ? (
                <>
                  {/* Create an array with today + next 3 days */}
                  {(() => {
                    // Extract today's temps from the forecast string (e.g., "H: 22°C  L: 15°C" or "H: -1°C  L: -3°C")
                    const forecastMatch = currentTimeInfo.weather.forecast.match(/H:\s*(-?\d+).*L:\s*(-?\d+)/);
                    if (!forecastMatch) {
                      return <div className="text-xs text-white/50 text-center">No forecast data available</div>;
                    }
                    const todayHigh = parseInt(forecastMatch[1]);
                    const todayLow = parseInt(forecastMatch[2]);

                    // Extract current temperature (e.g., "22°C" or "-2°C")
                    const currentTempMatch = currentTimeInfo.weather.temperature.match(/(-?\d+)/);
                    if (!currentTempMatch) {
                      return <div className="text-xs text-white/50 text-center">No temperature data available</div>;
                    }
                    const currentTemp = parseInt(currentTempMatch[1]);
                    
                    const todayEntry = {
                      dayName: 'Today',
                      icon: currentTimeInfo.weather.icon,
                      high: `${todayHigh}°`,
                      low: `${todayLow}°`,
                      highTemp: todayHigh,
                      lowTemp: todayLow,
                      currentTemp: currentTemp,
                      rainChance: 0
                    };
                    
                    const allDays = [todayEntry, ...currentTimeInfo.weekForecast.slice(0, 3)];
                    const minTemp = Math.min(...allDays.map(d => d.lowTemp));
                    const maxTemp = Math.max(...allDays.map(d => d.highTemp));
                    const tempRange = maxTemp - minTemp;
                    
                    return allDays.map((day, index) => {
                      const lowPosition = tempRange > 0 ? ((day.lowTemp - minTemp) / tempRange) * 100 : 0;
                      const highPosition = tempRange > 0 ? ((day.highTemp - minTemp) / tempRange) * 100 : 100;
                      const currentTempPosition = day.currentTemp && tempRange > 0 ? ((day.currentTemp - minTemp) / tempRange) * 100 : null;
                      
                      return (
                        <div key={index} className={cn(
                          "flex items-center gap-2",
                          isMobile ? "text-sm py-1" : "text-xs"
                        )}>
                          {/* Day name */}
                          <div className={day.dayName === 'Today' ? "text-white font-medium w-8" : "text-white/70 w-8"}>{day.dayName}</div>
                          
                          {/* Weather icon */}
                          <div className="flex justify-center w-4">
                            {day.icon === 'sun' || day.icon === 'clear-night' ? (
                              <Sun className="h-3 w-3 text-yellow-300" />
                            ) : day.icon === 'cloud' ? (
                              <TwoColorCloudSun className="h-3 w-3" />
                            ) : day.icon.includes('rain') || day.icon.includes('drizzle') || day.icon.includes('showers') ? (
                              <CloudRain className="h-3 w-3 text-blue-300" />
                            ) : day.icon === 'snow' ? (
                              <CloudSnow className="h-3 w-3 text-blue-200" />
                            ) : day.icon === 'storm' ? (
                              <Zap className="h-3 w-3 text-yellow-300" />
                            ) : (
                              <Cloud className="h-3 w-3 text-gray-300" />
                            )}
                          </div>
                          
                          {/* Temperature bar and values */}
                          <div className="flex-1 flex items-center gap-1">
                            <span className="text-white/50 w-5 text-right text-xs">{day.low}</span>
                            <div className="flex-1 relative h-1 bg-white/20 rounded-full mx-1">
                              <div 
                                className="absolute h-1 rounded-full"
                                style={{
                                  left: `${lowPosition}%`,
                                  width: `${Math.max(highPosition - lowPosition, 5)}%`,
                                  background: generateTemperatureGradient(day.lowTemp, day.highTemp)
                                }}
                              />
                              {/* Current temperature dot for today */}
                              {day.dayName === 'Today' && currentTempPosition !== null && (
                                <div 
                                  className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-sm"
                                  style={{
                                    left: `${currentTempPosition}%`,
                                    transform: 'translate(-50%, -50%)'
                                  }}
                                />
                              )}
                            </div>
                            <span className="text-white font-medium w-5 text-xs">{day.high}</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              ) : (
                <div className="text-xs text-white/50 text-center">No weather data available</div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherTimeInfoCard;